'use client';

import React, { useState } from 'react';
import ClientNav from '@/components/ClientNav';
import Link from 'next/link';

interface FAQItem {
  question: string;
  answer: string | React.ReactNode;
}

const faqs: FAQItem[] = [
  {
    question: 'How Is Pricing Determined?',
    answer:
      'Pricing is based on vehicle size, condition, and level of service required. All vehicles are evaluated prior to service to ensure accurate recommendations and transparent pricing.',
  },
  {
    question: 'Do You Offer Mobile Services?',
    answer:
      'Yes, we do offer mobile services. However, all services are also performed in a controlled in-studio environment to ensure the highest level of quality and consistency. Mobile services are only offered for our detail packages and paint corrections.',
  },
  {
    question: 'What is the difference between detailing and paint correction?',
    answer:
      'Detailing focuses on cleaning and restoring surfaces, while paint correction is a machine polishing process designed to remove swirl marks, scratches, and other defects to improve the overall finish.',
  },
  {
    question: 'Will all scratches and defects be removed?',
    answer:
      'Not all defects can be safely removed. Our goal is to achieve the maximum level of correction while preserving the integrity of the paint. Significant improvement is expected, but 100% removal is not always possible.',
  },
  {
    question: 'How Long Does a Service Take?',
    answer:
      'Service time varies depending on the condition of the vehicle and the level of work required. Most services range from a few hours to a full day. Advanced correction and coating services may require additional time.',
  },
  {
    question: 'Do You Offer Ceramic Coatings?',
    answer:
      'Yes. We offer professional ceramic coating services designed to provide long-term protection, increased gloss, and easier maintenance. Coatings are applied after proper paint preparation and correction.',
  },
  {
    question: 'How Long Does a Ceramic Coating Last?',
    answer:
      'Coating longevity depends on the product selected and how the vehicle is maintained. We offer options ranging from 1-year to 5-year protection.',
  },
  {
    question: 'Do I Need to Prepare My Vehicle Before My Appointment?',
    answer:
      'No preparation is required. However, removing personal belongings from the vehicle is recommended to allow for more efficient and thorough service.',
  },
  {
    question: 'Do You Clean Inside Glove Boxes and Center Consoles?',
    answer: (
      <>
        For client privacy and confidentiality, we do not access or clean the interior of glove boxes, center consoles, or any closed personal storage compartments.
        <br /><br />
        We recommend removing any personal or sensitive items prior to your appointment if you would like those areas addressed.
      </>
    ),
  },
  {
    question: 'Are Additional Fees Ever Applied?',
    answer:
      'Additional fees may apply for vehicles requiring extra time due to pet hair, excessive soiling, or hazardous conditions. All additional charges are discussed and approved before work begins.',
  },
  {
    question: 'How Do I Maintain My Vehicle After Service?',
    answer:
      'We recommend avoiding automatic brush washes and using proper hand wash techniques. Maintenance plans are available to help preserve your vehicle\'s condition over time.',
  },
  {
    question: 'How Do I Book an Appointment?',
    answer: (
      <>
        Appointments are scheduled by consultation to ensure the correct service is recommended.{' '}
        <Link href="/booking-flow" className="gold-text underline underline-offset-2 hover:opacity-80 transition-opacity">
          You can book on this site here!
        </Link>
      </>
    ),
  },
];

function FAQAccordionItem({ item, index }: { item: FAQItem; index: number }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left bg-card hover:bg-muted/40 transition-colors duration-150"
        aria-expanded={open}
      >
        <span className="text-sm font-semibold text-foreground leading-snug">{item.question}</span>
        <span
          className={`flex-shrink-0 w-6 h-6 rounded-full border border-border flex items-center justify-center transition-transform duration-200 ${open ? 'rotate-45' : ''}`}
          style={{ color: 'var(--primary)' }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </span>
      </button>
      {open && (
        <div className="px-6 pb-5 pt-1 bg-card border-t border-border">
          <p className="text-sm text-muted-foreground leading-relaxed">{item.answer}</p>
        </div>
      )}
    </div>
  );
}

export default function FAQPage() {
  return (
    <div className="min-h-screen" style={{ background: 'var(--background)' }}>
      <ClientNav />

      <main className="pt-24 pb-20 px-6">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-12 text-center">
            <p className="text-xs font-semibold tracking-widest uppercase gold-text mb-3">Support</p>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-md mx-auto">
              Everything you need to know before your appointment. Can't find an answer?{' '}
              <Link href="/booking-flow" className="gold-text hover:opacity-80 transition-opacity">
                Book a consultation
              </Link>{' '}
              and we'll walk you through it.
            </p>
          </div>

          {/* FAQ List */}
          <div className="flex flex-col gap-3">
            {faqs.map((item, index) => (
              <FAQAccordionItem key={index} item={item} index={index} />
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="mt-14 rounded-2xl border border-border p-8 text-center" style={{ background: 'var(--card)' }}>
            <p className="text-sm font-semibold text-foreground mb-1">Still have questions?</p>
            <p className="text-xs text-muted-foreground mb-5">
              We're happy to walk you through any service before you commit.
            </p>
            <Link
              href="/booking-flow"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg gold-gradient text-primary-foreground text-sm font-semibold transition-all duration-150 hover:opacity-90 active:scale-95"
            >
              Book a Consultation
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
